import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { Footer } from "@/components/layout/Footer";
import ModalLayout from "@/components/layout/ModalLayout";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatNumber } from "@/lib/utils";
import {
  usePurchaseByDriverList,
  usePurchaseByDriverDetails,
  usePurchaseByDriverTotals,
  fetchAllPurchaseByDriverDetails,
} from "@/hooks/settlement/se_purchaseByDriver/usePurchaseByDriver";
import { PurchaseByDriver, PurchaseByDriverDetail } from "@/interface/settlement/purchaseByDriver";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { Code } from "@/interface/standard/st_code/index";
import { getToday } from "@/lib/date";
import { createTransactionPdfBlob, TransactionItem } from "@/lib/transactionPdf";
import { toast } from "sonner";

export default function PurchaseByDriverPage() {
  const today = getToday();

  // PDF 미리보기 모달 상태
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [currentPdfFileName, setCurrentPdfFileName] = useState("");


  // 상단 검색 영역 (우측 패널용)
  const [company, setCompany] = useState("");
  const [itemSearchType, setItemSearchType] = useState("itemName");
  const [itemSearchValue, setItemSearchValue] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dateBtnValue, setDateBtnValue] = useState("today");

  // 좌측 패널 전용 검색 (조회 버튼과 무관, 즉시 필터링)
  const [leftSearchType, setLeftSearchType] = useState("vehicleNumber");
  const [leftSearchValue, setLeftSearchValue] = useState("");
  const [leftSearchParams, setLeftSearchParams] = useState({
    startDate: today,
    endDate: today,
    company: "",
    searchType: "",
    search: "",
  });

  // 정확히 일치 체크 (모든 검색에 적용)
  const [exactMatch, setExactMatch] = useState(false);

  // 좌우 패널 리사이즈 상태
  const [leftPanelWidth, setLeftPanelWidth] = useState(480);
  const isResizing = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 우측 패널용 검색 파라미터 (날짜 변경 시 실시간 반영)
  const transactionParams = useMemo(
    () => ({
      startDate,
      endDate,
      company,
      itemSearchType,
      itemSearch: itemSearchValue,
      itemExactMatch: exactMatch,
    }),
    [startDate, endDate, company, itemSearchType, itemSearchValue, exactMatch]
  );

  // 선택된 기사
  const [selectedDriverCode, setSelectedDriverCode] = useState<string>("");
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);

  // 좌측 패널 페이지네이션 상태
  const [leftCurrentPage, setLeftCurrentPage] = useState(0);
  const [leftAllData, setLeftAllData] = useState<(PurchaseByDriver & { no: number })[]>([]);
  const [leftLoadingMore, setLeftLoadingMore] = useState(false);
  const [leftTotalElements, setLeftTotalElements] = useState(0);

  // 우측 패널 페이지네이션 상태
  const [rightCurrentPage, setRightCurrentPage] = useState(0);
  const [rightAllData, setRightAllData] = useState<(PurchaseByDriverDetail & { no: number })[]>([]);
  const [rightLoadingMore, setRightLoadingMore] = useState(false);
  const [rightTotalElements, setRightTotalElements] = useState(0);

  // 회사 목록 (st_code COMPANY 하위 - 거래명세표 "귀하" 부분에 사용)
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();

  // 기사 목록 API (좌측 패널 - Page 응답)
  const { data: driversData, isLoading: driversLoading } = usePurchaseByDriverList({
    ...leftSearchParams,
    page: leftCurrentPage,
    size: 20,
  });

  // 거래 내역 API (우측 패널 - 기사 선택 시에만 조회)
  const { data: transactionsData, isLoading: transactionsLoading } = usePurchaseByDriverDetails(selectedDriverCode, {
    ...transactionParams,
    page: rightCurrentPage,
    size: 20,
  });

  // 거래 합계 API (Footer용 - 전체 검색 결과 기준)
  const { data: totalsData } = usePurchaseByDriverTotals(selectedDriverCode, transactionParams);

  // 좌측 패널: 데이터 누적 로직
  useEffect(() => {
    if (driversData?.content) {
      setLeftAllData(prev => {
        // 첫 페이지면 초기화
        if (leftCurrentPage === 0) {
          setLeftTotalElements(driversData.totalElements || 0);
          return driversData.content.map((item, index) => ({
            no: index + 1,
            ...item,
          }));
        }
        // 다음 페이지면 중복 제거 후 추가
        const newData = driversData.content.filter(
          newItem => !prev.some(existingItem => existingItem.driverCode === newItem.driverCode)
        );
        return [
          ...prev,
          ...newData.map((item, index) => ({
            no: prev.length + index + 1,
            ...item,
          })),
        ];
      });
    }
  }, [driversData, leftCurrentPage]);

  // 좌측 패널: 로딩 상태 업데이트
  useEffect(() => {
    if (driversData) {
      setLeftLoadingMore(false);
    }
  }, [driversData]);

  // 좌측 패널: 날짜/회사 변경 시 자동 갱신
  useEffect(() => {
    setLeftSearchParams(prev => ({
      ...prev,
      startDate,
      endDate,
      company,
    }));
    // 좌측 패널 데이터 초기화
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
    setSelectedDriverCode("");
    setSelectedRowIndex(-1);
    // 우측 패널 데이터 초기화
    setRightCurrentPage(0);
    setRightAllData([]);
    setRightTotalElements(0);
  }, [startDate, endDate, company]);

  // 좌측 패널: hasMore 계산
  const leftHasMore = useMemo(() => {
    if (!driversData) return false;
    return !driversData.last && leftAllData.length < leftTotalElements;
  }, [driversData, leftAllData.length, leftTotalElements]);

  // 좌측 패널: 무한 스크롤 콜백
  const handleLeftLoadMore = useCallback(async () => {
    if (!leftHasMore || leftLoadingMore) return;
    setLeftLoadingMore(true);
    setLeftCurrentPage(prev => prev + 1);
  }, [leftHasMore, leftLoadingMore]);

  // 우측 패널용 첫 마운트 여부 추적
  const isFirstMountRight = useRef(true);

  // 우측 패널: 검색 조건 또는 기사 변경 시 데이터 초기화 (첫 마운트 제외)
  useEffect(() => {
    if (isFirstMountRight.current) {
      isFirstMountRight.current = false;
      return;
    }
    setRightCurrentPage(0);
    setRightAllData([]);
    setRightTotalElements(0);
    setRightLoadingMore(false);
  }, [transactionParams, selectedDriverCode]);

  // 우측 패널: 데이터 누적 로직
  useEffect(() => {
    if (transactionsData?.content) {
      setRightAllData(prev => {
        // 첫 페이지면 초기화
        if (rightCurrentPage === 0) {
          setRightTotalElements(transactionsData.totalElements || 0);
          return transactionsData.content.map((item, index) => ({
            no: index + 1,
            ...item,
          }));
        }
        // 다음 페이지면 중복 제거 후 추가
        const newData = transactionsData.content.filter(
          newItem => !prev.some(existingItem => existingItem.saleSlipCode === newItem.saleSlipCode)
        );
        return [
          ...prev,
          ...newData.map((item, index) => ({
            no: prev.length + index + 1,
            ...item,
          })),
        ];
      });
    }
  }, [transactionsData, rightCurrentPage]);

  // 우측 패널: 로딩 상태 업데이트
  useEffect(() => {
    if (transactionsData) {
      setRightLoadingMore(false);
    }
  }, [transactionsData]);

  // 우측 패널: hasMore 계산
  const rightHasMore = useMemo(() => {
    if (!transactionsData) return false;
    return !transactionsData.last && rightAllData.length < rightTotalElements;
  }, [transactionsData, rightAllData.length, rightTotalElements]);

  // 우측 패널: 무한 스크롤 콜백
  const handleRightLoadMore = useCallback(async () => {
    if (!rightHasMore || rightLoadingMore) return;
    setRightLoadingMore(true);
    setRightCurrentPage(prev => prev + 1);
  }, [rightHasMore, rightLoadingMore]);

  // 회사 옵션 (전체 옵션 없이 무조건 선택)
  const companyOptions = useMemo(
    () =>
      companyCodeList.map((item: Code) => ({
        value: item.code || item.codeId?.toString() || "",
        label: item.name || "",
      })),
    [companyCodeList]
  );

  // 회사 목록 로드 시 기본값 설정 (정암로지스틱)
  useEffect(() => {
    if (companyCodeList.length > 0 && !company) {
      // "정암로지스틱" 찾기, 없으면 첫 번째 회사 선택
      const defaultCompany = companyCodeList.find((item: Code) => item.code === "01");
      const defaultValue = defaultCompany?.code || defaultCompany?.codeId?.toString() || companyCodeList[0]?.code || companyCodeList[0]?.codeId?.toString() || "";
      setCompany(defaultValue);
    }
  }, [companyCodeList, company]);

  // 좌측 패널 전용 검색 옵션
  const leftSearchTypeOptions = [
    { label: "차량번호", value: "vehicleNumber" },
    { label: "코드", value: "vehicleCode" },
  ];

  // 좌측 패널 검색 핸들러
  const handleLeftSearch = () => {
    setLeftSearchParams({
      startDate,
      endDate,
      company: "",
      searchType: leftSearchType,
      search: leftSearchValue,
    });
    // 데이터 초기화
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
    setSelectedDriverCode("");
    setSelectedRowIndex(-1);
  };

  const itemSearchTypeOptions = [
    { label: "품목명", value: "itemName" },
    { label: "품목코드", value: "itemCode" },
  ];

  const handleReset = () => {
    const resetDate = getToday();
    // 정암로지스틱 기본값 설정
    const defaultCompany = companyCodeList.find((item: Code) => item.name === "정암로지스틱");
    const defaultCompanyValue = defaultCompany?.code || defaultCompany?.codeId?.toString() || companyCodeList[0]?.code || companyCodeList[0]?.codeId?.toString() || "";
    setCompany(defaultCompanyValue);
    setItemSearchType("itemName");
    setItemSearchValue("");
    setExactMatch(false);
    setStartDate(resetDate);
    setEndDate(resetDate);
    setDateBtnValue("today");
    setSelectedDriverCode("");
    setSelectedRowIndex(-1);
    // 좌측 패널 검색 초기화
    setLeftSearchType("vehicleNumber");
    setLeftSearchValue("");
    setLeftSearchParams({
      startDate: resetDate,
      endDate: resetDate,
      company: defaultCompanyValue,
      searchType: "",
      search: "",
    });
    // 좌측 패널 데이터 초기화
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
  };

  // 좌측 차량 목록 컬럼
  // 우측 테이블과 헤더 높이를 맞추기 위해 group 추가
  const driverColumns: TableColumn[] = [
    { key: "no", header: "번호", bodyAlign: "center", width: "45px", minWidth: "45px", maxWidth: "45px" },
    {
      key: "driverCode",
      header: "코드",
      bodyAlign: "center",
      width: "70px",
      minWidth: "70px",
      maxWidth: "70px",
      group: "차량정보",
    },
    {
      key: "vehicleNumber",
      header: "차량번호",
      bodyAlign: "center",
      width: "1fr",
      minWidth: "90px",
      group: "차량정보",
    },
    {
      key: "companyName",
      header: "운송사",
      bodyAlign: "left",
      width: "1fr",
      minWidth: "80px",
      group: "차량정보",
    },
  ];

  // 우측 거래 내역 컬럼
  const transactionColumns: TableColumn[] = [
    { key: "no", header: "번호", bodyAlign: "center", width: "60px", minWidth: "60px", maxWidth: "60px" },
    { key: "transDate", header: "거래일자", bodyAlign: "center", width: "110px", minWidth: "110px", maxWidth: "110px" },
    {
      key: "inLocation",
      header: "상차지",
      bodyAlign: "left",
      width: "120px",
      minWidth: "120px",
      maxWidth: "120px",
      group: "업체",
    },
    {
      key: "companyName",
      header: "거래선",
      bodyAlign: "left",
      width: "150px",
      minWidth: "150px",
      maxWidth: "150px",
      group: "업체",
    },
    { key: "itemName", header: "품목명", bodyAlign: "left", width: "150px", minWidth: "150px", maxWidth: "150px" },
    {
      key: "qty",
      header: "수량",
      bodyAlign: "right",
      width: "80px",
      minWidth: "80px",
      maxWidth: "80px",
      render: (v: number) => (v ?? 0).toFixed(2),
    },
    {
      key: "unitPrice",
      header: "단가",
      bodyAlign: "right",
      width: "90px",
      minWidth: "90px",
      maxWidth: "90px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "supplyAmt",
      header: "공급가액",
      bodyAlign: "right",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "vatAmt",
      header: "부가세액",
      bodyAlign: "right",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "totalAmt",
      header: "합계",
      bodyAlign: "right",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    { key: "remark", header: "비고", bodyAlign: "left", width: "120px", minWidth: "120px", maxWidth: "120px" },
  ];

  // Footer 합계 (API에서 전체 검색 결과 기준으로 조회)
  const footerGrid = useMemo(() => {
    const totalQty = totalsData?.totalQty ?? 0;
    const totalSupply = totalsData?.totalSupplyAmt ?? 0;
    const totalVat = totalsData?.totalVatAmt ?? 0;
    const totalPurchase = totalsData?.totalPurchaseAmt ?? 0;

    return [
      [
        { label: "수량", value: totalQty.toFixed(2) },
        { label: "공급가액", value: formatNumber(totalSupply) },
        { label: "부가세액", value: formatNumber(totalVat) },
        { label: "총매입액", value: formatNumber(totalPurchase) },
      ],
    ];
  }, [totalsData]);

  // 기사 행 클릭 핸들러
  const handleDriverRowClick = (row: PurchaseByDriver & { no: number }, index: number) => {
    setSelectedDriverCode(row.driverCode);
    setSelectedRowIndex(index);
  };

  // 패널 리사이즈 핸들러
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const startX = e.clientX;
    const startWidth = leftPanelWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = e.clientX - startX;
      const newWidth = Math.max(280, Math.min(600, startWidth + delta));
      setLeftPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [leftPanelWidth]);

  // 선택된 차량 정보 가져오기
  const selectedVehicleInfo = useMemo(() => {
    return leftAllData.find(item => item.driverCode === selectedDriverCode);
  }, [leftAllData, selectedDriverCode]);

  // 선택된 차량 번호 가져오기
  const selectedVehicleNumber = useMemo(() => {
    return selectedVehicleInfo?.vehicleNumber || "";
  }, [selectedVehicleInfo]);

  // 거래명세표 버튼 클릭 - 바로 PDF 생성 (slipCompanyCode로 회사명 자동 매칭)
  const handleTransactionPdfClick = async () => {
    if (!selectedDriverCode || rightAllData.length === 0) {
      toast.error("차량을 선택하고 거래 내역이 있어야 합니다.");
      return;
    }

    if (!selectedVehicleInfo) {
      toast.error("차량 정보를 찾을 수 없습니다.");
      return;
    }

    try {
      // 전체 거래 내역 조회 (페이지네이션 없이)
      const allTransactions = await fetchAllPurchaseByDriverDetails(selectedDriverCode, {
        startDate,
        endDate,
        company,
        itemSearchType,
        itemSearch: itemSearchValue,
        itemExactMatch: exactMatch,
      });

      if (allTransactions.length === 0) {
        toast.error("거래 내역이 없습니다.");
        return;
      }

      // 첫 번째 거래의 slipCompanyCode로 회사명 찾기
      const slipCompanyCode = allTransactions[0]?.slipCompanyCode || "";
      const matchedCompany = companyCodeList.find(
        (c: Code) => c.code === slipCompanyCode || c.codeId?.toString() === slipCompanyCode
      );
      const clientName = matchedCompany?.name || "회사명 없음";

      // 거래 내역을 PDF용 데이터로 변환
      const items: TransactionItem[] = allTransactions.map(item => ({
        date: item.transDate ? item.transDate.substring(5).replace("-", "/") : "", // YYYY-MM-DD -> MM/DD
        inCompany: item.inLocation || "",
        outCompany: item.companyName || "",
        itemName: item.itemName || "",
        qty: item.qty || 0,
        unitPrice: item.unitPrice || 0,
        amount: item.supplyAmt || 0,
        vatAmount: item.vatAmt || 0,
        remark: item.remark || "",
      }));

      // 공급자 정보 (선택된 차량의 운송사 정보 - st_company_code 테이블)
      const supplier = {
        businessNumber: selectedVehicleInfo.bizNo || "",
        companyName: selectedVehicleInfo.companyName || "",
        representativeName: selectedVehicleInfo.ceoName || "",
        address: selectedVehicleInfo.address || "",
        businessType: selectedVehicleInfo.bizType || "",
        businessItem: selectedVehicleInfo.bizItem || "",
      };

      // PDF Blob 생성 및 미리보기 열기
      const blob = createTransactionPdfBlob({
        clientName,
        supplier,
        items,
      });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setCurrentPdfFileName(`거래명세표_${selectedVehicleNumber}`);
      setPreviewOpen(true);
    } catch (error) {
      console.error("전체 거래 내역 조회 실패:", error);
      toast.error("거래 내역을 불러오는데 실패했습니다.");
    }
  };

  // PDF 다운로드
  const handlePdfDownload = () => {
    if (!pdfBlobUrl) return;
    const todayDate = new Date();
    const dateStr = `${todayDate.getFullYear()}${String(todayDate.getMonth() + 1).padStart(2, "0")}${String(todayDate.getDate()).padStart(2, "0")}`;
    const link = document.createElement("a");
    link.href = pdfBlobUrl;
    link.download = `${currentPdfFileName}_${dateStr}.pdf`;
    link.click();
  };

  // PDF 인쇄
  const handlePdfPrint = () => {
    if (!pdfBlobUrl) return;
    const printWindow = window.open(pdfBlobUrl, "_blank");
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // 미리보기 모달 닫기
  const handlePreviewClose = () => {
    setPreviewOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          {/* 좌측 패널 전용 검색 (기사명/기사코드) */}
          <SelectBox
            label="구분"
            className="w-28"
            value={leftSearchType}
            onChange={setLeftSearchType}
            options={leftSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={leftSearchValue}
            onChange={e => setLeftSearchValue(e.target.value)}
            placeholder="입력"
            onKeyDown={e => {
              if (e.key === "Enter") handleLeftSearch();
            }}
          />
          <div className="mr-1" />
          <Button variant="search" onClick={handleLeftSearch}>
            검색
          </Button>
          <div className="mr-3" />

          {/* 우측 패널용 검색 */}
          <SelectBox label="회사" className="w-40" value={company} onChange={setCompany} options={companyOptions} />
          <div className="mr-3" />
          <SelectBox
            label="구분"
            className="w-28"
            value={itemSearchType}
            onChange={setItemSearchType}
            options={itemSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={itemSearchValue}
            onChange={e => setItemSearchValue(e.target.value)}
            placeholder="입력"
          />
          <div className="mr-3" />

          <DateBox
            label="거래일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />

          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-end mb-2">
          <div className="flex gap-2">
            <Button
              variant="etc"
              onClick={handleTransactionPdfClick}
              disabled={!selectedDriverCode || rightAllData.length === 0}
            >
              거래명세표
            </Button>
            <PrintButton data={rightAllData} columns={transactionColumns} title="기사별매입현황" skipPermissionCheck />
            <ExcelButton data={rightAllData} columns={transactionColumns} fileName="기사별매입현황" skipPermissionCheck />
          </div>
        </div>

        {/* 본문 - 좌우 분할 */}
        <div ref={containerRef} className="flex-1 min-h-0 flex">
          {/* 좌측: 차량 목록 */}
          <div style={{ width: leftPanelWidth, minWidth: 280, maxWidth: 600 }} className="flex-shrink-0">
            <Table
              className="h-full"
              columns={driverColumns}
              data={leftAllData}
              showRowNumber={false}
              maxHeight="100%"
              onRowClick={handleDriverRowClick}
              highlightOnClick
              selectedRows={selectedRowIndex >= 0 ? [selectedRowIndex] : []}
              loading={driversLoading && leftAllData.length === 0}
              emptyMessage="차량을 검색하세요"
              infiniteScroll={true}
              initialLoadCount={20}
              loadMoreCount={20}
              hasMore={leftHasMore}
              onLoadMore={handleLeftLoadMore}
              loadingMore={leftLoadingMore}
              enablePagination={true}
              pageSize={20}
              onPageChange={setLeftCurrentPage}
              currentPage={leftCurrentPage}
            />
          </div>

          {/* 리사이즈 핸들 */}
          <div
            onMouseDown={handleMouseDown}
            className="w-2 flex-shrink-0 cursor-col-resize hover:bg-blue-200 active:bg-blue-300 transition-colors flex items-center justify-center group"
          >
            <div className="w-0.5 h-8 bg-gray-300 group-hover:bg-blue-400 rounded-full" />
          </div>

          {/* 우측: 거래 내역 */}
          <div className="flex-1 min-w-0">
            <Table
              className="h-full"
              columns={transactionColumns}
              data={rightAllData}
              showRowNumber={false}
              resizable
              maxHeight="100%"
              loading={transactionsLoading && rightAllData.length === 0}
              emptyMessage="기사를 선택하세요"
              infiniteScroll={true}
              initialLoadCount={20}
              loadMoreCount={20}
              hasMore={rightHasMore}
              onLoadMore={handleRightLoadMore}
              loadingMore={rightLoadingMore}
              enablePagination={true}
              pageSize={20}
              onPageChange={setRightCurrentPage}
              currentPage={rightCurrentPage}
            />
          </div>
        </div>
      </main>

      {/* 푸터 총계 */}
      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>

      {/* 회사 선택 모달 (거래명세표 "귀하" 부분) */}
      {/* PDF 미리보기 모달 */}
      <ModalLayout
        isOpen={previewOpen}
        onClose={handlePreviewClose}
        title="거래명세표 미리보기"
        maxWidth="max-w-[90vw]"
      >
        <div className="flex flex-col h-[80vh]">
          <div className="flex justify-end gap-2 p-4 border-b">
            <Button variant="etc" onClick={handlePdfPrint}>
              인쇄
            </Button>
            <Button variant="etc" onClick={handlePdfDownload}>
              다운로드
            </Button>
          </div>
          <div className="flex-1 overflow-auto">
            {pdfBlobUrl && (
              <iframe src={pdfBlobUrl} className="w-full h-full" title="거래명세표 미리보기" />
            )}
          </div>
        </div>
      </ModalLayout>
    </div>
  );
}
